import React, {FC, useCallback} from 'react'
import {Button, ButtonPriority, Card, HoverStyles, PaddingModes, Text} from 'wix-ui-tpa/cssVars'
import {useTranslation} from '@wix/yoshi-flow-editor'
import {Experience as ExperienceProto} from '@wix/ambassador-table-reservations-v1-experience/types'
import {useSettings} from '@wix/tpa-settings/react'

import {settingsParams} from '../../../settingsParams'
import {DEFAULT_CURRENCY} from '../../../../../utils/constants'
import {withMediaStorageUrl} from '../../../../../utils/images-gallery'
import {convertMinutesToHoursAndMinutes} from '../../../../../utils/date'
import {useGetLayoutSize} from '../../../../../components-shared/LayoutSizeProvider'
import {ExperienceSchedule} from '../../../../../components-shared/ExperienceSchedule'
import {ExperiencePrice} from '../../../../../components-shared/ExperiencePrice'

import {classes, st} from './Experience.st.css'

interface Props {
  regionalSettings?: string
  experience: ExperienceProto
  showCoverImages: boolean
  handleGoToExperience: (experience: ExperienceProto) => void
}

export const Experience: FC<Props> = ({
  regionalSettings,
  experience,
  showCoverImages,
  handleGoToExperience,
}) => {
  const {t} = useTranslation()
  const {layoutSize} = useGetLayoutSize()
  const settings = useSettings()

  const {configuration, currency} = experience

  const showExperienceImage: boolean = settings.get(settingsParams.showExperienceImage)
  const showExperienceSummary: boolean = settings.get(settingsParams.showExperienceSummary)
  const showExperienceDuration: boolean = settings.get(settingsParams.showExperienceDuration)
  const showExperienceDate: boolean = settings.get(settingsParams.showExperienceDate)
  const showExperiencePrice: boolean = settings.get(settingsParams.showExperiencePrice)

  const reserveNowButtonText: string = settings.get(settingsParams.reserveNowButtonText)
  const learnMoreButtonText: string = settings.get(settingsParams.learnMoreButtonText)

  const imageId = configuration?.displayInfo?.coverImage?.id

  const businessSchedule = configuration?.onlineReservations?.businessSchedule
  const durationInMinutes = businessSchedule?.durationInMinutes

  const handleExperienceClick = useCallback(() => {
    if (experience.id) {
      handleGoToExperience(experience)
    }
  }, [experience, handleGoToExperience])

  const experiencePageHref =
    experience.experiencePageUrl?.url ?? experience.experiencePageUrl?.relativePath

  const content = (
    <>
      {showExperienceImage && showCoverImages ? (
        <div className={st(classes.imageContainer)}>
          <img
            className={st(classes.image)}
            src={imageId ? withMediaStorageUrl(imageId) : undefined}
            alt={configuration?.displayInfo?.coverImage?.altText || ''}
          />
        </div>
      ) : null}
      <div className={st(classes.content)}>
        <Text tagName="h2" className={st(classes.name, classes.textAlignment)}>
          {configuration?.displayInfo?.name}
        </Text>
        {showExperienceSummary ? (
          <Text className={st(classes.summary, classes.textAlignment)}>
            {configuration?.displayInfo?.shortDescription}
          </Text>
        ) : null}
        <div className={st(classes.description)}>
          <div className={st(classes.textAlignment)}>
            {showExperienceDuration && durationInMinutes ? (
              <Text className={st(classes.details)}>
                {t(
                  'uou-reservations.experiences-list.duration',
                  convertMinutesToHoursAndMinutes(durationInMinutes),
                )}
              </Text>
            ) : null}
            {showExperienceDuration && durationInMinutes && showExperienceDate ? (
              <Text className={st(classes.details)}> | </Text>
            ) : null}
            {showExperienceDate ? (
              <ExperienceSchedule
                className={st(classes.details)}
                businessSchedule={businessSchedule}
                regionalSettings={regionalSettings}
              />
            ) : null}
          </div>
          {showExperiencePrice ? (
            <ExperiencePrice
              className={st(classes.details, classes.textAlignment)}
              currency={currency ?? DEFAULT_CURRENCY}
              paymentPolicy={configuration?.paymentPolicy}
            />
          ) : null}
        </div>
        <div className={st(classes.buttonContainer)}>
          <Button
            as="span"
            className={st(classes.button)}
            priority={ButtonPriority.primary}
            hoverStyle={HoverStyles.underline}
            paddingMode={PaddingModes.explicitPaddings}
          >
            {businessSchedule ? reserveNowButtonText : learnMoreButtonText}
          </Button>
        </div>
      </div>
    </>
  )

  return (
    <Card className={st(classes.card, {[layoutSize]: true})}>
      <Card.Container className={st(classes.cardContainer)}>
        {experiencePageHref ? (
          <a className={st(classes.clickableContainer)} href={experiencePageHref}>
            {content}
          </a>
        ) : (
          <div className={st(classes.clickableContainer)} onClick={handleExperienceClick}>
            {content}
          </div>
        )}
      </Card.Container>
    </Card>
  )
}
